/**
 * useCompetitionState - Local UI State Management
 *
 * Single responsibility: Managing local UI state for CompetitionView.
 * Handles modal states and leaderboard visibility.
 */

import { useState, useCallback } from 'react';

interface ModalState {
  quickPick: boolean;
  favPick: boolean;
  clear: boolean;
  /** The "Picks Submitted" post-entry popup, opened by a successful submit. */
  success: boolean;
}

/**
 * Hook for managing local UI state in CompetitionView
 *
 * @returns UI state and state setters
 */
export function useCompetitionState() {
  const [modals, setModals] = useState<ModalState>({
    quickPick: false,
    favPick: false,
    clear: false,
    success: false,
  });

  const [isLeaderboardOpen, setIsLeaderboardOpen] = useState(false);

  // Modal handlers
  const openQuickPickModal = useCallback(() => {
    setModals(prev => ({ ...prev, quickPick: true }));
  }, []);

  const closeQuickPickModal = useCallback(() => {
    setModals(prev => ({ ...prev, quickPick: false }));
  }, []);

  const openFavPickModal = useCallback(() => {
    setModals(prev => ({ ...prev, favPick: true }));
  }, []);

  const closeFavPickModal = useCallback(() => {
    setModals(prev => ({ ...prev, favPick: false }));
  }, []);

  const openClearModal = useCallback(() => {
    setModals(prev => ({ ...prev, clear: true }));
  }, []);

  const closeClearModal = useCallback(() => {
    setModals(prev => ({ ...prev, clear: false }));
  }, []);

  const openSuccessModal = useCallback(() => {
    setModals(prev => ({ ...prev, success: true }));
  }, []);

  const closeSuccessModal = useCallback(() => {
    setModals(prev => ({ ...prev, success: false }));
  }, []);

  // Leaderboard handlers
  const openLeaderboard = useCallback(() => {
    setIsLeaderboardOpen(true);
  }, []);

  const closeLeaderboard = useCallback(() => {
    setIsLeaderboardOpen(false);
  }, []);

  return {
    // State
    modals,
    isLeaderboardOpen,

    // Modal handlers
    openQuickPickModal,
    closeQuickPickModal,
    openFavPickModal,
    closeFavPickModal,
    openClearModal,
    closeClearModal,
    openSuccessModal,
    closeSuccessModal,

    // Leaderboard handlers
    openLeaderboard,
    closeLeaderboard,
  };
}
